/**
 * Supabase Image Transformation Utilities
 * Generates optimized image URLs with proper sizing for crisp display
 */

const SUPABASE_PROJECT_REF = 'nvmyabvxofnfvrnbehkm';
const SUPABASE_STORAGE_URL = `https://${SUPABASE_PROJECT_REF}.supabase.co/storage/v1`;

interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Transforms a Supabase storage URL to use image transformations
 * Doubles the size for retina display support
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  options: ImageTransformOptions = {}
): string | null {
  if (!url) return null;
  
  // Only transform Supabase storage URLs
  if (!url.includes(SUPABASE_PROJECT_REF) || !url.includes('/storage/v1/')) {
    return url;
  }

  // Default options with 2x for retina
  const {
    width,
    height,
    quality = 85,
    resize = 'cover'
  } = options;

  // Build transformation params
  const params = new URLSearchParams();
  
  if (width) params.set('width', String(width * 2)); // 2x for retina
  if (height) params.set('height', String(height * 2)); // 2x for retina
  params.set('quality', String(quality));
  params.set('resize', resize);

  // Convert object URL to render URL for transformations
  // From: .../storage/v1/object/public/bucket/path
  // To: .../storage/v1/render/image/public/bucket/path?params
  const renderUrl = url.replace('/storage/v1/object/', '/storage/v1/render/image/');
  
  // Add params
  const separator = renderUrl.includes('?') ? '&' : '?';
  return `${renderUrl}${separator}${params.toString()}`;
}

/**
 * Get optimized company logo URL
 * Standard sizes for different display contexts
 */
export function getOptimizedLogoUrl(
  url: string | null | undefined,
  size: 'sm' | 'md' | 'lg' | 'xl' = 'md'
): string | null {
  const sizeMap = {
    sm: { width: 28, height: 28 },   // Header small logo
    md: { width: 32, height: 32 },   // Mobile header, list items
    lg: { width: 56, height: 56 },   // Larger displays
    xl: { width: 80, height: 80 },   // Full logo display
  };

  return getOptimizedImageUrl(url, sizeMap[size]);
}

/**
 * Get optimized profile picture URL
 */
export function getOptimizedProfileUrl(
  url: string | null | undefined,
  size: 'sm' | 'md' | 'lg' = 'md'
): string | null {
  const sizeMap = {
    sm: { width: 24, height: 24 },
    md: { width: 40, height: 40 },
    lg: { width: 96, height: 96 },
  };

  return getOptimizedImageUrl(url, sizeMap[size]);
}
